import { definedObject } from "@/lib/helper/object";
import { availableCaterings } from "@/lib/helper/caterings";
import { HostSearch } from "@/graphql/HostSearch.ts";
import { useDebounce } from "@/lib/helper/debounce";
import { useSearchStore } from "@/store/search-store";

/**
 * @typeDef {{
 *   from?:number;
 *   to?:number;
 * }} NumericRange
 */
/** @typeDef {("Relevancy" | "Price" | "Nights" | "Persons" | "Score" | "Stars" | "Discount" | "Distance")} Sorting **/
/** @typeDef {("grid" | "list" | "map")} ViewMode **/
/**
 * @typeDef {{
 *  viewMode?:ViewMode;
 *  query?:string;
 *  page?:number;
 *  pageSize?:number;
 *  sorting?:Sorting
 *  sortDirection?:("asc"|"desc")
 *  Nights?:NumericRange
 *  Persons?:NumericRange
 *  CurrentPrice?:NumericRange
 *  Stars?:NumericRange
 *  Caterings?:string[]
 *  Location?:any
 *  CategoryId?:string[]
 *  SearchCriteria?:string[]
 * }} SearchOptions
 */
/**
 * @typeDef {{
 *  viewMode?:string;
 *  query?:string;
 *  sorting?:Sorting
 *  sortDirection?:("asc"|"desc")
 *  fromNights?:number
 *  toNights?:number
 *  fromPersons?:number
 *  toPersons?:number
 *  fromPrice?:number
 *  toPrice?:number
 *  fromStars?:number
 *  toStars?:number
 *  caterings?:string[]
 *  searchCriteria?:string[]
 * }} SearchQuery
 */


/**
 * Construct a query object from the given object to be used as route.query value
 * @param { SearchOptions } options
 * @returns { SearchQuery }
 */
export const makeSearchQuery = (options) => {
  return definedObject({
    viewMode: options.viewMode,
    query: options.query,
    sorting: options.sorting,
    sortDirection: options.sortDirection,
    fromNights: options?.Nights?.from,
    toNights: options?.Nights?.to,
    fromPersons: options?.Persons?.from,
    toPersons: options?.Persons?.to,
    fromPrice: options?.CurrentPrice?.from,
    toPrice: options?.CurrentPrice?.to,
    fromStars: options?.Stars?.from,
    toStars: options?.Stars?.to,
    caterings: options?.Caterings?.length ? options.Caterings.join(",") : undefined,
    searchCriteria: options?.SearchCriteria?.length ? options.SearchCriteria.join(",") : undefined,
    lat: options?.Location?.Latitude,
    lng: options?.Location?.Longitude,
    locationName: options?.Location?.Name,
    distance: options?.Location?.Distance
  });
};
/**
 * Remove all search related properties from the given route.query object
 * @param { Record<string,any> } query
 * @returns { Record<string,any> }
 */
export const stripSearchQuery = (query) => {
  query = query || {};
  const keys = ["viewMode", "query", "sorting", "sortDirection", "fromNights", "toNights", "fromPersons", "toPersons", "fromPrice", "toPrice", "fromStars", "toStars", "caterings", "searchCriteria", "location"];
  const r = { ...query };
  keys.forEach((key) => {
    if (!(key in r)) return;
    delete r[key];
  });
  return r;
};

export const getSearchQuery = (query, defaultValues = {}) => {

  const parseRange = (from, to) => {
    if ((isNaN(from) || typeof from !== "number") && (isNaN(to) || typeof to !== "number")) return undefined;
    return definedObject({ from, to });
  };

  query = query || {};

  const Location = definedObject({
    Latitude: query?.lat ? parseFloat(query?.lat) : undefined,
    Longitude: query?.lng ? parseFloat(query?.lng) : undefined,
    Distance: query?.lat && query?.lng ? parseFloat(query?.distance) || 150000 : undefined
  });
  const SearchCriteria = query?.searchCriteria ? (query.searchCriteria.includes(",") ? query.searchCriteria.split(",") : [query.searchCriteria]) : [];

  return definedObject({
    viewMode: query.viewMode || "grid",
    locationName: query.locationName,
    query: query.query,
    sorting: query.sorting,
    sortDirection: query.sortDirection,
    Nights: parseRange(parseInt(query.fromNights), parseInt(query.toNights)),
    Persons: parseRange(parseInt(query?.fromPersons), parseInt(query?.toPersons)),
    CurrentPrice: parseRange(parseFloat(query?.fromPrice), parseFloat(query?.toPrice)),
    Stars: parseRange(parseInt(query?.fromStars), parseInt(query?.toStars)),
    Caterings: query.caterings ? query.caterings.split(",") : availableCaterings.map((entry) => entry.value),
    SearchCriteria,
    Location,
    ...defaultValues
  });
};


export const fetchSuggestions = async (apollo, term, delay = 1000) => {
  const debounce = useDebounce();
  term = term?.trim();
  if (!term?.length || term.length < 3) return [];
  return new Promise((resolve, reject) => {
    debounce(async () => {
      await apollo.query({
        query: HostSearch.Queries.Suggestions,
        variables: { term }
      }).then((response) => {
        const suggestions = response?.data?.hostSearchSuggestions || [];
        resolve(suggestions);
      }).catch((e) => reject(e));
    }, delay);
  });
};

export const fetchLocations = async (apollo, term, delay = 1000) => {
  const debounce = useDebounce();
  term = term?.trim();
  if (!term?.length) return [];
  return new Promise((resolve, reject) => {
    debounce(async () => {
      await apollo.query({
        query: HostSearch.Queries.FindLocation,
        variables: { term }
      }).then((response) => {
        const suggestedLocations = response?.data?.findLocation || [];
        resolve(suggestedLocations);
      }).catch((e) => reject(e));
    }, delay);
  });
};

export const defaultRangeLimits = {
  Nights: {
    from: 1,
    to: 14
  },
  Price: {
    from: 1,
    to: 3000
  },
  Persons: {
    from: 1,
    to: 14
  },
  Stars: {
    from: 0,
    to: 6
  }
};

/**
 *
 * @param {import("apollo-client")} apollo
 * @param {SearchOptions} options
 * @return {Promise<any>}
 */
export const searchHosts = async (apollo, options) => {
  const viewMode = options.viewMode || "grid";
  delete options.viewMode;
  const caterings = options.Caterings;
  if (options?.Location?.Name) {
    delete options.Location.Name;
  }
  if (options.query) options.query = decodeURIComponent(options.query).trim();
  if (options.query === "a&o") options.query = "\"a&o\"";
  const response = await apollo
    .query({
      query: viewMode === "map" ? HostSearch.Queries.MapHostsSearch : HostSearch.Queries.HostsSearch,
      variables: {
        options: {
          ...options,
          sorting: options.sorting || "Price",
          sortDirection: options.sortDirection || "asc",
          Caterings: [...new Set(caterings)]
        }
      }
    })
    .then((response) => {
      const data = response?.data?.hostsSearch;
      return {
        query: options?.query,
        requestId: data?.requestId,
        autocorrect: data?.autocorrect,
        totalHosts: data?.totalHosts || 0,
        page: data?.page || 1,
        pageSize: data?.pageSize || 1,
        maxPage: data?.maxPage || 1,
        hosts: data?.hosts || data?.documents || [],
        searchCriteria: data?.searchCriteria || []
      };
    });

  useSearchStore().setLastSearch(response);
  return response;
};
